import React, { useEffect, useState } from "react";
import {
  Card, Table, Button, Space, Col, Row, App,
  Modal, Descriptions, Tag, Image, Typography, Tooltip, Grid,
  message, Select
} from "antd";

const { useBreakpoint } = Grid;
import {
  EyeOutlined, FilePdfOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import {
  updateAttendanceStatus,
  reclassifyAttendance,
  fetchAllEmployees,
  fetchApproveAttendanceReport,
} from "../attendanceOverviewThunk";
import {
  selectEmployees,
  selectApproveAttendanceReport,
} from "../attendanceOverviewSelectors";
import { selectAuthInfo } from "../../Auth/authSelectors";
import dayjs from "dayjs";
import SearchListAutoComplete from "../../../components/SearchListAutoComplete";
import { ReloadOutlined } from "@ant-design/icons";
import "./attendanceDetails.css";
import CommonDateRangeFilter from "../../../components/common/filters/CommonDateRangeFilter";
import { formatToCompanyTimezone, formatToCompanyDate } from "../../../lib/functions";

const { Text, Link } = Typography;


const ApproveAttendanceTab = () => {
  const dispatch  = useDispatch();
  const screens = useBreakpoint();
  const employees = useSelector(selectEmployees);
  const tableData = useSelector(selectApproveAttendanceReport);
  const authInfo = useSelector(selectAuthInfo);
  const isIndia = authInfo?.country === "India";
  const { modal } = App.useApp();

  const [filters, setFilters] = useState({
    fromDate: null, toDate: null, empId: null, status: null,
  });
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [viewVisible, setViewVisible] = useState(false);
  const [viewRecord, setViewRecord]   = useState(null);
  const [reclassifyRecord, setReclassifyRecord] = useState(null);
  const [reclassifyTarget, setReclassifyTarget] = useState(null);
  const [reclassifying, setReclassifying] = useState(false);
const isLeaveRequest =
  !!viewRecord?.leave_portion ||
  !!viewRecord?.leave_portion_label;
  useEffect(() => { dispatch(fetchAllEmployees()); }, [dispatch]);

  useEffect(() => {
    dispatch({ type: "attendanceOverview/clearApproveAttendanceReport" });
    dispatch(fetchApproveAttendanceReport(filters));
  }, [filters]);

  const handleApproveReject = async (record, status) => {
    try {      
      await dispatch(updateAttendanceStatus({
        user_id:  record.id,
        status,
        fromDate: record.from_date,
        toDate:   record.to_date,
      })).unwrap();
      message.success({
        content: "Attendance updated successfully",
        duration: 1,
      });
      dispatch({ type: "attendanceOverview/clearApproveAttendanceReport" });
      dispatch(fetchApproveAttendanceReport(filters));
    } catch (err) {
      modal.error({
        title:   "Failed to update Attendance",
        content: err?.message || "An unexpected error occurred",
      });
    }
  };

  const reclassifyOptions = [
    isIndia
      ? { label: "Casual Leave", value: "casual_leave" }
      : { label: "Annual Leave", value: "annual_leave" },
    { label: "Sick Leave", value: "sick_leave" },
    { label: "Flexi Leave", value: "flexi_leave" },
  ];

  const handleReclassify = async () => {
    if (!reclassifyTarget || !reclassifyRecord) return;
    setReclassifying(true);
    try {
      await dispatch(reclassifyAttendance({
        user_id: reclassifyRecord.id,
        fromDate: reclassifyRecord.from_date,
        toDate: reclassifyRecord.to_date,
        new_attendance_type: reclassifyTarget,
      })).unwrap();
      message.success({ content: "Reclassified successfully", duration: 1 });
      setReclassifyRecord(null);
      setReclassifyTarget(null);
      dispatch({ type: "attendanceOverview/clearApproveAttendanceReport" });
      dispatch(fetchApproveAttendanceReport(filters));
    } catch (err) {
      modal.error({
        title: "Failed to reclassify",
        content: err?.message || "An unexpected error occurred",
      });
    } finally {
      setReclassifying(false);
    }
  };

  const formatText = (value) => {
    if (!value) return "";
    return value.split("_")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");
  };

  const parseAttachments = (attachments) => {
    if (!attachments) return [];
    if (Array.isArray(attachments)) return attachments;
    try { return JSON.parse(attachments); } catch { return []; }
  };

  const isPdf = (url) => url?.toLowerCase().endsWith(".pdf");

  const renderStatus = (value) => {
    const map = {
      pending:   { label: "Pending Approval", bg: "#EDEEFF", color: "#5258E4" },
      approved:  { label: "Approved",          bg: "#CEF8E0", color: "#007A7D" },
      rejected:  { label: "Rejected",          bg: "#FFEBE7", color: "#D31510" },
      cancelled: { label: "Cancelled",         bg: "#F2F2F2", color: "#595959" },
    };
    const s = map[value] || { label: formatText(value), bg: "transparent", color: "black" };
    return (
      <div style={{
        backgroundColor: s.bg, color: s.color,
        display: "flex", alignItems: "center", gap: 6,
        borderRadius: 12, padding: "2px 8px",
        fontWeight: 500, width: "fit-content",
      }}>
        <div style={{ backgroundColor: s.color, borderRadius: "50%", width: 6, height: 6 }} />
        {s.label}
      </div>
    );
  };

  // ── Leave balance — plain/standard, no colors ──
  const renderLeaveBalance = (record) => (
    <Row gutter={16} style={{ marginBottom: 24 }}>
      <Col span={8}>
        <div style={{
          borderRadius: 8,
          padding: "12px 16px",
          textAlign: "center",
          border: "1px solid #d9d9d9",
          background: "#fafafa",
        }}>
          <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>
            Annual Leave Available
          </div>
          <div style={{ fontSize: 24, fontWeight: 700, color: "#262626" }}>
            {record.annual_leave_days ?? "-"}
          </div>
          <div style={{ fontSize: 11, color: "#aaa" }}>days / year</div>
        </div>
      </Col>
      <Col span={8}>
        <div style={{
          borderRadius: 8,
          padding: "12px 16px",
          textAlign: "center",
          border: "1px solid #d9d9d9",
          background: "#fafafa",
        }}>
          <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>
            Previously Taken
          </div>
          <div style={{ fontSize: 24, fontWeight: 700, color: "#262626" }}>
            {Number(record.total_leave_taken ?? 0).toFixed(1)}
          </div>
          <div style={{ fontSize: 11, color: "#aaa" }}>
            {Number(record.total_leave_days ?? 0)} full
            {" + "}
            {Number(record.total_short_leave_days ?? 0).toFixed(1)} short
          </div>
        </div>
      </Col>
      <Col span={8}>
        <div style={{
          borderRadius: 8,
          padding: "12px 16px",
          textAlign: "center",
          border: "1px solid #d9d9d9",
          background: "#fafafa",
        }}>
          <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>
            Remaining Balance
          </div>
          <div style={{ fontSize: 24, fontWeight: 700, color: "#262626" }}>
            {Number(record.remaining_leave_balance ?? 0).toFixed(1)}
          </div>
          <div style={{ fontSize: 11, color: "#aaa" }}>days left</div>
        </div>
      </Col>
    </Row>
  );

  const renderAttachments = (attachments) => {
    const files = parseAttachments(attachments);
    if (!files.length) return <Text type="secondary">No attachments uploaded</Text>;

    return (
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginTop: 8 }}>
        {files.map((url, idx) =>
          isPdf(url) ? (
            <div key={idx} style={{
              border: "1px solid #d9d9d9", borderRadius: 8,
              padding: "12px 16px", display: "flex",
              alignItems: "center", gap: 8, background: "#fafafa",
            }}>
              <FilePdfOutlined style={{ fontSize: 24, color: "#ff4d4f" }} />
              <div>
                <div style={{ fontSize: 12, color: "#888" }}>PDF Document {idx + 1}</div>
                <Link href={url} target="_blank" rel="noopener noreferrer">View PDF</Link>
              </div>
            </div>
          ) : (
            <div key={idx} style={{
              border: "1px solid #d9d9d9",
              borderRadius: 8, overflow: "hidden", width: 120,
            }}>
              <Image
                src={url}
                alt={`Attachment ${idx + 1}`}
                width={120} height={90}
                style={{ objectFit: "cover" }}
                placeholder
              />
            </div>
          )
        )}
      </div>
    );
  };

  const columns = [
    { title: "No.", render: (_, __, i) => i + 1,},
    {
      title: "Employee", dataIndex: "user_name", key: "user_name",
      render: (val) => <span style={{ whiteSpace: "nowrap" }}>{val}</span>,
    },
    {
      title: "User Roles", dataIndex: "roles", key: "roles",
      render: (value) => {
        const formatted = value
          ? value.split(",").map((role) =>
              formatText(role.includes("_") ? role.split("_").slice(1).join(" ") : role.trim())
            ).join(", ")
          : "-";
        return <span style={{ color: "#0265DC", fontWeight: 500, fontSize: 12 }}>{formatted}</span>;
      },
    },
    {
      title: "Status", dataIndex: "status", key: "status",
      render: (value) => renderStatus(value),
    },
    {
      title: "Branch Name", dataIndex: "name", key: "name",
      render: (v) => v || "-",
    },
    {
      title: "Attendance", dataIndex: "attendance", key: "attendance",
      render: (_, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span>{formatText(record.attendance)}</span>
          {record.is_regularized === 1 && (
            <span style={{
              background: "#FFF7E6", color: "#D48806",
              padding: "1px 8px", borderRadius: 10, fontSize: 11, fontWeight: 500,
            }}>Regularized</span>
          )}
          {parseAttachments(record.attachments).length > 0 && (
            <Tag color="blue" style={{ fontSize: 11 }}>
              📎 {parseAttachments(record.attachments).length}
            </Tag>
          )}
          {record.leave_portion_label && (
            <Tag color="orange">
              {record.leave_portion_label}
            </Tag>
          )}
          
        </div>
      ),
    },
    // {
    //   title: "Remaining Balance", key: "remaining",
    //   render: (_, record) => {
    //     const bal = Number(record.remaining_leave_balance ?? 0);
    //     return (
    //       <span style={{ fontWeight: 600, color: bal <= 2 ? "#cf1322" : "#389e0d" }}>
    //         {bal.toFixed(1)} days
    //       </span>
    //     );
    //   },
    // },
    {
      title: "From Date", dataIndex: "from_date", key: "from_date",
      defaultSortOrder: "descend",

      sorter: (a, b) =>
        new Date(a.from_date) - new Date(b.from_date),

      render: (v) => (v ? formatToCompanyTimezone(v, "YYYY-MMM-DD") : "-"),
    },
    {
      title: "To Date",
      dataIndex: "to_date",
      key: "to_date",

      sorter: (a, b) =>
        new Date(a.to_date) - new Date(b.to_date),

      render: (v) => (v ? formatToCompanyTimezone(v, "YYYY-MMM-DD") : "-"),
    },

    // ── View column — separate from Action ──
    {
      title: "View",
      key: "view",
      render: (_, record) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          style={{ padding: 0 }}
          onClick={() => { setViewRecord(record); setViewVisible(true); }}
        >
          View
        </Button>
      ),
    },

    // ── Action column — only Approve / Reject ──
    {
      title: "Action",
      key: "actions",
      render: (_, record) => (
        record.status === "pending" ? (
          record.leave_request_id ? (
            <Tooltip title="This leave request is approved via the Leave Approval workflow.">
              <span style={{ color: "#aaa", fontSize: 13 }}>Managed by workflow</span>
            </Tooltip>
          ) : (
            <Space>
              <Button
                type="link"
                data-testid="approve_btn"
                style={{ color: "green", padding: 0 }}
                onClick={() => handleApproveReject(record, "approved")}
              >
                Approve
              </Button>
              <span>|</span>
              <Button
                type="link"
                style={{ color: "red", padding: 0 }}
                data-testid="reject_btn"
                onClick={() => handleApproveReject(record, "rejected")}
              >
                Reject
              </Button>
            </Space>
          )
        ) : record.attendance === "emergency_leave" ? (
          <Button
            type="link"
            style={{ padding: 0 }}
            onClick={() => { setReclassifyRecord(record); setReclassifyTarget(null); }}
          >
            Reclassify
          </Button>
        ) : (
          <span style={{ color: "#aaa", fontSize: 13 }}>
            {formatText(record.status)}
          </span>
        )
      ),
    },
  ];

  const filterStatus = [
    { status: "pending",   display_text: "Pending"   },
    { status: "approved",  display_text: "Approved"  },
    { status: "rejected",  display_text: "Rejected"  },
    { status: "cancelled", display_text: "Cancelled" },
  ];

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <Card>
      {/* Filters */}
      <div className="page-filters mb-4 shadow-md rounded-xl" style={{ marginBottom: 12 }}>
        <Row gutter={[16, 16]} wrap style={{ alignItems: "center", marginBottom: 12 }}>
          <Col xs={24} sm={12} md={8} lg={6} xl={5} xxl={4}>
            <label>All Users</label>
            <SearchListAutoComplete
              className="attendanceDetailsSearchList"
              optionsList={employees.map((c) => ({ sale_id: c.id, display_text: c.user_name }))}
              onSelect={(obj) => setFilters((p) => ({ ...p, empId: obj?.sale_id }))}
              placeholder="Account"
              showAddNew={false}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6} xl={5} xxl={4}>
            <label>All Status</label>
            <SearchListAutoComplete
              className="attendanceDetailsSearchList"
              optionsList={filterStatus.map((c) => ({ sale_id: c.status, display_text: c.display_text }))}
              onSelect={(obj) => setFilters((p) => ({ ...p, status: obj?.sale_id }))}
              placeholder="Account"
              showAddNew={false}
              data-testid="filter_status"
            />
          </Col>
          <CommonDateRangeFilter
          filters={filters}
          onChange={(fromDate, toDate) => {
            handleFilterChange("fromDate", fromDate);
            handleFilterChange("toDate", toDate);
          }}
        />
        <Tooltip title="Refresh the list">
            <Button
              icon={<ReloadOutlined />}
              onClick={() => dispatch(fetchApproveAttendanceReport(filters))}
              shape="circle"
              title="Refresh"
              style={{margin: "23px 0 0"}}
              />
              </Tooltip>
        </Row>
        <style>{`
        @media (min-width: 1200px) and (max-width: 1560px) {
  .page-filters .ant-row .ant-col {
    flex: 0 0 23.33% !important;
    max-width: 23.33% !important;
  }
}

            @media (min-width: 768px) and (max-width: 1200px){
              .page-filters .ant-row .ant-col {
                flex: 0 0 50% !important;
                max-width: 50% !important;
              }
            }
             @media (min-width: 768px) {
  :where(.css-dev-only-do-not-override-1j5i8gz).ant-col-md-4 {
    max-width: none !important;
    flex: none !important;
  }
          `}</style>
      </div>      

      {/* Table */}
      <Table
        rowKey={(record) => `${record.id}_${record.attendance}_${record.status}_${record.from_date}_${record.to_date}`}
        columns={columns}
        dataSource={tableData}
        data-testid="approve_attendance_table"
        pagination={{
          current: pagination.current, pageSize: pagination.pageSize,
          showSizeChanger: true, showQuickJumper: true,
          pageSizeOptions: ["10", "20", "50", "100"],
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
          onChange: (page, pageSize) => setPagination({ current: page, pageSize }),
          onShowSizeChange: (_, size) => setPagination({ current: 1, pageSize: size }),
        }}
        bordered
        size="middle"
        scroll={{ x: "max-content" }}
      />

      {/* ── View Details Modal ── */}
      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <EyeOutlined />
            <span>  {isLeaveRequest
        ? "Leave Request Details"
        : "Attendance Approval Details"}</span>
            {viewRecord && renderStatus(viewRecord.status)}
          </div>
        }
        open={viewVisible}
        onCancel={() => { setViewVisible(false); setViewRecord(null); }}
        footer={
          viewRecord?.status === "pending" && !viewRecord?.leave_request_id ? (
            <Space>
              <Button onClick={() => { setViewVisible(false); setViewRecord(null); }}>
                Close
              </Button>
              <Button
                danger
                onClick={() => {
                  handleApproveReject(viewRecord, "rejected");
                  setViewVisible(false);
                }}
              >
                Reject
              </Button>
              <Button
                type="primary"
                style={{ background: "#52c41a", borderColor: "#52c41a" }}
                onClick={() => {
                  handleApproveReject(viewRecord, "approved");
                  setViewVisible(false);
                }}
              >
                Approve
              </Button>
            </Space>
          ) : (
            <Button onClick={() => { setViewVisible(false); setViewRecord(null); }}>
              Close
            </Button>
          )
        }
        width={screens.md ? 720 : "95%"}
        centered
      >
        {viewRecord && (
          <>
            {/* ── Leave Balance Summary ── */}
         {renderLeaveBalance(viewRecord)}

            <Descriptions
              bordered
              column={screens.md ? 2 : 1}
              size="small"
              layout={screens.md ? "horizontal" : "vertical"}
              style={{ marginBottom: 24 }}
            >
              <Descriptions.Item label="Employee" span={screens.md ? 2 : 1}>
                <strong>{viewRecord.user_name || "-"}</strong>
              </Descriptions.Item>

              <Descriptions.Item label="Attendance Type">
                {formatText(viewRecord.attendance)}
                {viewRecord.is_regularized === 1 && (
                  <Tag color="orange" style={{ marginLeft: 8 }}>Regularized</Tag>
                )}
              </Descriptions.Item>

              <Descriptions.Item label="Branch">
                {viewRecord.name || "-"}
              </Descriptions.Item>

              <Descriptions.Item label="From Date">
                {viewRecord.from_date ? formatToCompanyDate(viewRecord.from_date) : "-"}
              </Descriptions.Item>

              <Descriptions.Item label="To Date">
                {viewRecord.to_date ? formatToCompanyDate(viewRecord.to_date) : "-"}
              </Descriptions.Item>
{!isLeaveRequest && (
  <>
    <Descriptions.Item label="Check In">
      {viewRecord.check_in
        ? dayjs(viewRecord.check_in, "HH:mm:ss").format("hh:mm:ss A")
        : "-"}
    </Descriptions.Item>

    <Descriptions.Item label="Check Out">
      {viewRecord.check_out
        ? dayjs(viewRecord.check_out, "HH:mm:ss").format("hh:mm:ss A")
        : "-"}
    </Descriptions.Item>
  </>
)}

              <Descriptions.Item label="Notes" span={screens.md ? 2 : 1}>
                {viewRecord.notes || "-"}
              </Descriptions.Item>
            </Descriptions>

            {/* ── Attachments ── */}
            <div>
              <Text strong style={{ fontSize: 14 }}>📎 Attachments</Text>
              <div style={{ marginTop: 8 }}>
                {renderAttachments(viewRecord.attachments)}
              </div>
            </div>
          </>
        )}
      </Modal>

      {/* ── Reclassify Emergency Leave ── */}
      <Modal
        title="Reclassify Emergency Leave"
        open={!!reclassifyRecord}
        onCancel={() => { setReclassifyRecord(null); setReclassifyTarget(null); }}
        onOk={handleReclassify}
        okText="Convert"
        confirmLoading={reclassifying}
        okButtonProps={{ disabled: !reclassifyTarget }}
      >
        {reclassifyRecord && (
          <>
            <p>
              <Text strong>{reclassifyRecord.user_name || "This employee"}</Text>
              {" "}was marked Emergency Leave (Unpaid) from{" "}
              <Text strong>{formatToCompanyDate(reclassifyRecord.from_date)}</Text>
              {" "}to{" "}
              <Text strong>{formatToCompanyDate(reclassifyRecord.to_date)}</Text>.
            </p>
            <p>Convert it to:</p>
            <Select
              style={{ width: "100%" }}
              placeholder="Select leave type"
              options={reclassifyOptions}
              value={reclassifyTarget}
              onChange={setReclassifyTarget}
            />
            <p style={{ marginTop: 12, color: "#888", fontSize: 12 }}>
              This won't be blocked even if it exceeds the employee's remaining balance for the selected type — review their balance beforehand if needed.
            </p>
          </>
        )}
      </Modal>
    </Card>
  );
};

export default ApproveAttendanceTab;